import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, X, Settings, Loader2, AlertCircle, CheckCircle2, HelpCircle, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

/**
 * NotificationPermissionBanner
 *
 * Persistent in-app prompt that surfaces whenever a signed-in user running
 * inside the installed app (TWA / PWA) doesn't actually have working push
 * notifications. The "working" state requires several layers to all be
 * `true` simultaneously:
 *
 *   • Service worker registered
 *   • Active push subscription on this device
 *   • `Notification.permission === "granted"`
 *   • Android-level POST_NOTIFICATIONS toggle on (when in TWA)
 *
 * Any layer being wrong yields silent failure. The banner now ships with an
 * expandable diagnostic that shows the live state of every layer so when a
 * user reports "notifications not working", they (or we, via screenshot)
 * can immediately see WHICH layer is the blocker.
 *
 * Behaviour branches:
 *   1. `Notification.permission === "default"` → CTA fires the Android
 *      runtime permission dialog (wired via the DelegationService).
 *   2. `Notification.permission === "denied"` → OS refuses to re-prompt;
 *      banner explains how to enable manually in Settings.
 *   3. `granted` + no subscription → silent re-subscribe attempt; if that
 *      fails, the diagnostic shows the underlying error.
 *
 * Dismissable for 7 days via localStorage.
 */

const DISMISS_KEY = "taladraw_push_banner_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function isPushSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function isStandaloneApp() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) return true;
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function detectEnv() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  return "Desktop";
}

async function gatherDiagnostic() {
  const d = {
    env: detectEnv(),
    standalone: isStandaloneApp(),
    pushSupported: isPushSupported(),
    notificationPermission: typeof Notification !== "undefined" ? Notification.permission : "missing",
    permissionsApiState: "unknown",
    serviceWorkerRegistered: false,
    serviceWorkerActive: false,
    pushSubscribed: false,
    vapidConfigured: !!(process.env.REACT_APP_VAPID_PUBLIC_KEY),
  };

  // Permissions API — Android-layer source of truth on Chromium.
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const s = await navigator.permissions.query({ name: "notifications" });
      d.permissionsApiState = s.state;
    } catch {
      d.permissionsApiState = "unsupported";
    }
  } else {
    d.permissionsApiState = "unsupported";
  }

  // Service worker / push subscription
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      d.serviceWorkerRegistered = !!reg;
      d.serviceWorkerActive = !!(reg && reg.active);
      if (reg) {
        try {
          const sub = await reg.pushManager.getSubscription();
          d.pushSubscribed = !!sub;
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }

  return d;
}

function StateChip({ value, label, ok, warn }) {
  let color = "text-zinc-300 border-zinc-700 bg-zinc-800/60";
  let Icon = HelpCircle;
  if (ok) {
    color = "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
    Icon = CheckCircle2;
  } else if (warn) {
    color = "text-rose-300 border-rose-500/40 bg-rose-500/10";
    Icon = AlertCircle;
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${color}`}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.5} />
      {label}: <span className="font-mono">{String(value)}</span>
    </span>
  );
}

export default function NotificationPermissionBanner() {
  const { user } = useAuth();
  const [permission, setPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "denied"
  );
  const [subscribed, setSubscribed] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);

  const refreshState = useCallback(async () => {
    if (!isPushSupported()) return;
    setPermission(Notification.permission);
    const d = await gatherDiagnostic();
    setDiag(d);
    setSubscribed(d.pushSubscribed);
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshState();
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (at && Date.now() - at < DISMISS_WINDOW_MS) setDismissed(true);
    } catch { /* ignore */ }
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshState();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user, refreshState]);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (!navigator.permissions || !navigator.permissions.query) return;
    let status = null;
    const handler = () => refreshState();
    navigator.permissions
      .query({ name: "notifications" })
      .then((s) => {
        status = s;
        s.addEventListener("change", handler);
      })
      .catch(() => { /* not supported */ });
    return () => {
      if (status) {
        try { status.removeEventListener("change", handler); } catch { /* ignore */ }
      }
    };
  }, [refreshState]);

  const allow = async () => {
    if (requesting) return;
    setRequesting(true);
    try {
      const { subscribeToPush } = await import("@/lib/pushNotifications");
      await subscribeToPush();
      toast.success("Notifications enabled");
      await refreshState();
    } catch {
      if (Notification.permission === "denied") {
        toast.error("Permission blocked — enable it in Settings");
      } else {
        toast.error("Couldn't enable notifications. Tap Diagnose for details.");
      }
      await refreshState();
    } finally {
      setRequesting(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const copyDiagnostic = async () => {
    if (!diag) return;
    const text = `TalaDraw notification diagnostic
env: ${diag.env}
standalone: ${diag.standalone}
pushSupported: ${diag.pushSupported}
Notification.permission: ${diag.notificationPermission}
Permissions API: ${diag.permissionsApiState}
serviceWorkerRegistered: ${diag.serviceWorkerRegistered}
serviceWorkerActive: ${diag.serviceWorkerActive}
pushSubscribed: ${diag.pushSubscribed}
vapidConfigured: ${diag.vapidConfigured}
userAgent: ${navigator.userAgent}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Diagnostic copied — paste it to support");
    } catch {
      toast.error("Couldn't copy. Long-press the details to copy manually.");
    }
  };

  if (!user) return null;
  if (!isPushSupported()) return null;
  if (!isStandaloneApp()) return null;
  if (dismissed) return null;
  if (permission === "granted" && subscribed) return null;

  const isDenied = permission === "denied";
  // Detect the genuinely broken sub-state: permission says granted but no
  // push subscription survived. Common after Chrome data wipe / OS update.
  const isGrantedButBroken = permission === "granted" && !subscribed;

  // Identify likely root cause for the inline hint line under the title.
  let rootCauseHint = null;
  if (diag) {
    if (isDenied) {
      rootCauseHint = "Android system permission is OFF for this app.";
    } else if (isGrantedButBroken) {
      if (!diag.serviceWorkerActive) {
        rootCauseHint = "The background service worker isn't running — try force-closing and reopening the app.";
      } else if (!diag.vapidConfigured) {
        rootCauseHint = "Push server keys missing on this build.";
      } else {
        rootCauseHint = "Permission granted but no active push subscription — tap Allow to re-subscribe.";
      }
    }
  }

  return (
    <div
      className="px-3 pt-2 pointer-events-none"
      data-testid="notif-permission-banner"
    >
      <div className="mx-auto max-w-md rounded-xl border border-amber-500/30 bg-amber-500/10 backdrop-blur shadow-lg pointer-events-auto">
        <div className="flex items-start gap-3 p-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            {isDenied ? (
              <BellOff className="w-4 h-4 text-amber-400" strokeWidth={2.5} />
            ) : (
              <Bell className="w-4 h-4 text-amber-400" strokeWidth={2.5} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold leading-snug">
              {isDenied ? "Notifications are blocked" :
               isGrantedButBroken ? "Re-enable push for this device" :
               "Turn on notifications"}
            </div>
            <div className="text-zinc-300 text-xs mt-0.5 leading-relaxed">
              {isDenied
                ? "Enable them in Settings → Apps → TalaDraw → Notifications, then return to the app."
                : "Get instant alerts when ATMs near you go live."}
            </div>
            {rootCauseHint && (
              <div className="mt-1 text-[11px] text-amber-300 leading-relaxed">
                <strong>Likely cause:</strong> {rootCauseHint}
              </div>
            )}

            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {isDenied ? (
                <button
                  type="button"
                  onClick={dismiss}
                  className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 text-black text-xs font-semibold px-3 py-1.5 hover:bg-amber-400 transition-colors"
                  data-testid="notif-permission-banner-settings"
                >
                  <Settings className="w-3 h-3" strokeWidth={2.5} />
                  Got it
                </button>
              ) : (
                <button
                  type="button"
                  onClick={allow}
                  disabled={requesting}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#00FF94] text-black text-xs font-semibold px-3 py-1.5 hover:bg-[#00FF94]/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  data-testid="notif-permission-banner-allow"
                >
                  {requesting ? (
                    <><Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.5} />Requesting…</>
                  ) : (
                    <><Bell className="w-3 h-3" strokeWidth={2.5} />Allow notifications</>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={() => setDiagOpen((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md text-amber-300 hover:text-amber-200 text-[11px] font-semibold px-2 py-1"
                data-testid="notif-permission-banner-diagnose"
              >
                {diagOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {diagOpen ? "Hide diagnostic" : "Diagnose"}
              </button>
            </div>

            {diagOpen && diag && (
              <div className="mt-2 rounded-lg border border-amber-500/30 bg-black/40 p-2.5" data-testid="notif-permission-banner-diagnostic">
                <div className="flex flex-wrap gap-1.5">
                  <StateChip
                    label="env"
                    value={`${diag.env}${diag.standalone ? " (app)" : ""}`}
                    ok={diag.standalone}
                  />
                  <StateChip
                    label="Notification.permission"
                    value={diag.notificationPermission}
                    ok={diag.notificationPermission === "granted"}
                    warn={diag.notificationPermission === "denied"}
                  />
                  <StateChip
                    label="OS permission"
                    value={diag.permissionsApiState}
                    ok={diag.permissionsApiState === "granted"}
                    warn={diag.permissionsApiState === "denied"}
                  />
                  <StateChip
                    label="service worker"
                    value={diag.serviceWorkerActive ? "active" : (diag.serviceWorkerRegistered ? "registered" : "missing")}
                    ok={diag.serviceWorkerActive}
                    warn={!diag.serviceWorkerRegistered}
                  />
                  <StateChip
                    label="push subscription"
                    value={diag.pushSubscribed ? "yes" : "no"}
                    ok={diag.pushSubscribed}
                    warn={!diag.pushSubscribed && diag.notificationPermission === "granted"}
                  />
                  <StateChip
                    label="VAPID key"
                    value={diag.vapidConfigured ? "configured" : "missing"}
                    ok={diag.vapidConfigured}
                    warn={!diag.vapidConfigured}
                  />
                </div>
                <button
                  type="button"
                  onClick={copyDiagnostic}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 text-[10px] font-semibold px-2 py-1"
                  data-testid="notif-permission-banner-copy-diag"
                >
                  <Copy className="w-2.5 h-2.5" strokeWidth={2.5} />
                  Copy diagnostic
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 p-1 rounded-md hover:bg-amber-500/20 text-zinc-400 hover:text-zinc-200 transition-colors"
            aria-label="Dismiss"
            data-testid="notif-permission-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
